import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  ClipboardCheck, Calendar, Clock, CheckCircle, XCircle, 
  AlertCircle, RefreshCw, PenTool, UserCheck, MapPin,
  GraduationCap, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

const StudentAttendancePage = ({ onMenuClick }) => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sign modal
  const [showSignModal, setShowSignModal] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [signatureType, setSignatureType] = useState('button'); // 'button' or 'drawn'
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const fetchSheets = useCallback(async () => {
    try {
      const response = await api.get('/attendance/student/sheets');
      setSheets(response.data);
    } catch (error) {
      console.error('Erreur chargement émargements:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  // Canvas for drawn signature
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e1b4b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  useEffect(() => {
    if (showSignModal && signatureType === 'drawn') {
      setTimeout(initCanvas, 100);
    }
  }, [showSignModal, signatureType]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    initCanvas();
  };

  const openSignModal = (sheet) => {
    setSelectedSheet(sheet);
    setShowSignModal(true);
    setSignatureType('button');
  };

  const submitSignature = async () => {
    let sigData = null;
    
    if (signatureType === 'drawn') {
      const canvas = canvasRef.current;
      sigData = canvas?.toDataURL('image/png');
    }
    
    try {
      await api.post(`/attendance/student/sheets/${selectedSheet.id}/sign`, {
        signature_data: sigData,
        signature_type: signatureType
      });
      toast.success('Présence enregistrée !');
      setShowSignModal(false);
      fetchSheets();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la signature';
      toast.error(msg);
    }
  };

  const getStatusBadge = (status, signed) => {
    if (signed) {
      return (
        <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
          <CheckCircle className="w-4 h-4" />
          Signé
        </span>
      );
    }
    
    const config = {
      present: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Présent' },
      absent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Absent' },
      excused: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Excusé' },
      late: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Retard' }
    };
    const c = config[status] || config.absent;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getSheetStatusBadge = (status) => {
    if (status === 'in_progress') {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 animate-pulse">
          Émargement ouvert
        </span>
      );
    }
    return null;
  };

  // Categorize sheets
  const today = new Date().toISOString().split('T')[0];
  const openSheets = sheets.filter(s => s.status === 'in_progress' && !s.my_signed);
  const todaySheets = sheets.filter(s => s.event_date === today);
  const pastSheets = sheets.filter(s => s.event_date < today);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-violet-600" />
            Mes émargements
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Signez votre présence aux cours
          </p>
        </div>

        {/* Open for signing */}
        {openSheets.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-blue-600">
              <PenTool className="w-5 h-5" />
              À signer maintenant
            </h2>
            <div className="space-y-4">
              {openSheets.map(sheet => (
                <Card key={sheet.id} className="border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          {getSheetStatusBadge(sheet.status)}
                        </div>
                        <h3 className="font-bold text-xl text-slate-900 dark:text-white">
                          {sheet.event_title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(sheet.event_date).toLocaleDateString('fr-FR')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {sheet.start_time} - {sheet.end_time}
                          </span>
                          {sheet.group_name && (
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" />
                              {sheet.group_name}
                            </span>
                          )}
                          {sheet.trainer_name && (
                            <span className="flex items-center gap-1">
                              <GraduationCap className="w-4 h-4" />
                              {sheet.trainer_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Button 
                        onClick={() => openSignModal(sheet)}
                        size="lg"
                        className="bg-blue-600 hover:bg-blue-700 text-lg px-8"
                      >
                        <PenTool className="w-5 h-5 mr-2" />
                        Signer ma présence
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Today's sessions */}
        {todaySheets.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-600" />
              Aujourd'hui
            </h2>
            <div className="space-y-3">
              {todaySheets.map(sheet => (
                <Card key={sheet.id} className={sheet.my_signed ? 'border-emerald-200' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${sheet.my_signed ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                          {sheet.my_signed ? (
                            <CheckCircle className="w-6 h-6 text-emerald-600" />
                          ) : (
                            <ClipboardCheck className="w-6 h-6 text-slate-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold">{sheet.event_title}</h3>
                          <div className="flex items-center gap-3 text-sm text-slate-600">
                            <span>{sheet.start_time} - {sheet.end_time}</span>
                            {sheet.trainer_name && <span>• {sheet.trainer_name}</span>}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {getStatusBadge(sheet.my_status, sheet.my_signed)}
                        
                        {sheet.status === 'in_progress' && !sheet.my_signed && (
                          <Button 
                            onClick={() => openSignModal(sheet)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Signer
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Past sessions */}
        {pastSheets.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Historique</h2>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50 dark:bg-slate-800">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Cours</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastSheets.slice(0, 20).map(sheet => (
                      <tr key={sheet.id} className="border-b">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {new Date(sheet.event_date).toLocaleDateString('fr-FR')}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium">{sheet.event_title}</p>
                          <p className="text-sm text-slate-500">{sheet.start_time} - {sheet.end_time}</p>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {getStatusBadge(sheet.my_status, sheet.my_signed)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {sheets.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun émargement à afficher</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Sign Modal */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-violet-600" />
              Signer ma présence
            </DialogTitle>
            <DialogDescription>
              {selectedSheet?.event_title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Visual preview */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3">
                <h4 className="text-white font-semibold text-base">{selectedSheet?.event_title}</h4>
                {selectedSheet?.course_name && (
                  <p className="text-violet-200 text-sm">{selectedSheet.course_name}</p>
                )}
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-300 font-medium">
                      {selectedSheet && new Date(selectedSheet.event_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-300 font-medium">
                      {selectedSheet?.start_time} – {selectedSheet?.end_time}
                    </span>
                  </div>
                </div>
                {selectedSheet?.trainer_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <GraduationCap className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-300">Formateur : <strong>{selectedSheet.trainer_name}</strong></span>
                  </div>
                )}
                {selectedSheet?.group_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <BookOpen className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-300">Groupe : <strong>{selectedSheet.group_name}</strong></span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Signature type selection */}
            <div>
              <label className="text-sm font-medium mb-3 block">Mode de signature</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setSignatureType('button')}
                  className={`flex-1 p-4 border-2 rounded-lg text-center transition-all ${
                    signatureType === 'button' 
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <UserCheck className="w-8 h-8 mx-auto mb-2 text-violet-600" />
                  <p className="font-medium">Validation</p>
                  <p className="text-xs text-slate-500">Clic simple</p>
                </button>
                <button
                  onClick={() => setSignatureType('drawn')}
                  className={`flex-1 p-4 border-2 rounded-lg text-center transition-all ${
                    signatureType === 'drawn' 
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <PenTool className="w-8 h-8 mx-auto mb-2 text-violet-600" />
                  <p className="font-medium">Signature</p>
                  <p className="text-xs text-slate-500">Dessiner</p>
                </button>
              </div>
            </div>
            
            {/* Drawn signature canvas */}
            {signatureType === 'drawn' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Dessinez votre signature</label>
                <div className="border-2 border-dashed rounded-lg p-2 bg-white">
                  <canvas
                    ref={canvasRef}
                    width={350}
                    height={120}
                    className="cursor-crosshair w-full touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <Button variant="link" size="sm" onClick={clearCanvas} className="mt-1">
                  Effacer
                </Button>
              </div>
            )}
            
            {/* Confirmation message */}
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm text-amber-700 dark:text-amber-300">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              En signant, je confirme ma présence à ce cours.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={submitSignature}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmer ma présence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentAttendancePage;
